import type {
  DiffKind,
  FrameworkContent,
  ItemChange,
  PlanRole,
} from '@/lib/api/discovery-frameworks';

/**
 * Pure helpers for Firm settings → Discovery frameworks (T-0184). Counts
 * here are counted by code from the content the API returned; nothing is
 * estimated.
 */

export { formatDate, plural } from '@/components/requirement-library/library-logic';

export type FrameworkTab = 'areas' | 'types' | 'questionnaires' | 'rules' | 'history';
export const FRAMEWORK_TABS: readonly FrameworkTab[] = [
  'areas',
  'types',
  'questionnaires',
  'rules',
  'history',
];

/** URL state: `?framework=<id>&tab=<tab>`. */
export interface FrameworkSearch {
  framework?: string;
  tab?: FrameworkTab;
  /** A process whose reference map is open (T-0189), on the Areas tab. */
  process?: string;
}

export function parseFrameworkSearch(search: Record<string, unknown>): FrameworkSearch {
  const out: FrameworkSearch = {};
  const { framework, tab, process } = search;
  if (typeof framework === 'string' && framework.length > 0) out.framework = framework;
  if (typeof process === 'string' && process.length > 0) out.process = process;
  if (typeof tab === 'string' && (FRAMEWORK_TABS as readonly string[]).includes(tab)) {
    out.tab = tab as FrameworkTab;
  }
  return out;
}

export const ANSWER_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'rating', label: 'Rating 1–5' },
  { value: 'single', label: 'Pick one' },
  { value: 'multiple', label: 'Pick several' },
] as const;

export function answerTypeLabel(type: string): string {
  return ANSWER_TYPES.find((t) => t.value === type)?.label ?? type;
}

export const isChoiceType = (type: string): boolean => type === 'single' || type === 'multiple';

export const DURATIONS = [30, 45, 60, 90];
export const QUESTIONNAIRE_MINUTES = [5, 10, 12, 15, 20, 30];
export const SOURCES_MIN = 1;
export const SOURCES_MAX = 4;

/** The choices for a select, with the current value kept even if it is not a standard one. */
export function choicesWith(options: readonly number[], value: number): number[] {
  return Array.from(new Set([...options, value])).sort((a, b) => a - b);
}

export const isActive = (item: { status: string }): boolean => item.status === 'active';

/**
 * What a list shows. Read mode shows the method as it is: active items.
 * Edit mode also shows retired ones, struck through, so they can be
 * restored (rule 3: nothing is deleted).
 */
export function shown<T extends { status: string }>(items: readonly T[], editing: boolean): T[] {
  return editing ? [...items] : items.filter(isActive);
}

export interface ContentCounts {
  areas: number;
  interviewTypes: number;
  questionnaires: number;
  /** Active questions of active questionnaires. */
  questions: number;
}

export function contentCounts(content: FrameworkContent): ContentCounts {
  const questionnaires = content.questionnaires.filter(isActive);
  return {
    areas: content.areas.filter(isActive).length,
    interviewTypes: content.interview_types.filter(isActive).length,
    questionnaires: questionnaires.length,
    questions: questionnaires.reduce((n, q) => n + q.questions.filter(isActive).length, 0),
  };
}

/** Comma-separated text to a list: trimmed, blanks dropped, repeats (any case) dropped. */
export function parseList(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(',')) {
    const item = raw.trim().replace(/\s+/g, ' ');
    if (!item || seen.has(item.toLowerCase())) continue;
    seen.add(item.toLowerCase());
    out.push(item);
  }
  return out;
}

/** Job-title keywords are stored in lower case (E12-F01 scenario 7). */
export function parseKeywords(text: string): string[] {
  return parseList(text.toLowerCase());
}

export function rolesText(roles: readonly PlanRole[]): string {
  return roles.map((r) => (r.optional ? `${r.role} (optional)` : r.role)).join(', ');
}

// ---------------------------------------------------------------------------
// What the draft changes
// ---------------------------------------------------------------------------

const KIND_WORDS: Record<DiffKind, [string, string]> = {
  areas: ['area', 'areas'],
  interview_types: ['interview type', 'interview types'],
  questionnaires: ['questionnaire', 'questionnaires'],
  questions: ['question', 'questions'],
  processes: ['process to map', 'processes to map'],
};
const KIND_ORDER: DiffKind[] = [
  'areas',
  'interview_types',
  'questionnaires',
  'questions',
  'processes',
];

/** True when the draft changes the sources rule against the published version. */
function rulesChanged(content: FrameworkContent, publishedSources: number | null): boolean {
  return (
    content.version?.state === 'draft' &&
    publishedSources != null &&
    content.version.sources_required !== publishedSources
  );
}

/** How many changes the draft holds against the current version, rules included. */
export function draftChangeCount(
  content: FrameworkContent,
  publishedSources: number | null,
): number {
  const counts = content.version?.changes ?? {};
  let n = rulesChanged(content, publishedSources) ? 1 : 0;
  for (const c of Object.values(counts)) n += c.added + c.changed + c.retired;
  return n;
}

/** One line per kind of change, for the publish dialog: "2 areas changed". */
export function changeSummary(
  content: FrameworkContent,
  publishedSources: number | null,
): string[] {
  const out: string[] = [];
  const counts = content.version?.changes ?? {};
  for (const kind of KIND_ORDER) {
    const c = counts[kind];
    if (!c) continue;
    const [one, many] = KIND_WORDS[kind];
    for (const [n, verb] of [
      [c.added, 'added'],
      [c.changed, 'changed'],
      [c.retired, 'retired'],
    ] as const) {
      if (n > 0) out.push(`${n} ${n === 1 ? one : many} ${verb}`);
    }
  }
  if (rulesChanged(content, publishedSources)) {
    out.push(
      `independent sources each area needs: ${publishedSources} → ${content.version?.sources_required}`,
    );
  }
  return out;
}

export interface DraftDifference {
  action: 'Added' | 'Changed' | 'Retired' | 'Restored';
  what: string;
  code: string | null;
  title: string;
}

interface Comparable {
  id: string;
  status: string;
  code?: string;
  change?: ItemChange;
}

function compare<T extends Comparable>(
  out: DraftDifference[],
  what: string,
  before: readonly T[],
  after: readonly T[],
  title: (item: T) => string,
): void {
  const earlier = new Map(before.map((item) => [item.id, item]));
  for (const item of after) {
    const old = earlier.get(item.id);
    const base = { what, code: item.code ?? null, title: title(item) };
    if (!old) out.push({ action: 'Added', ...base });
    else if (old.status !== item.status) {
      out.push({ action: item.status === 'retired' ? 'Retired' : 'Restored', ...base });
    } else if (stableJson(old) !== stableJson(item)) out.push({ action: 'Changed', ...base });
  }
}

/** JSON without the draft markers and nested lists, which are compared on their own. */
function stableJson(item: object): string {
  const rest: Record<string, unknown> = { ...(item as Record<string, unknown>) };
  delete rest['change'];
  delete rest['questions'];
  delete rest['library_modules'];
  return JSON.stringify(rest);
}

/**
 * What changed in the draft between two loads of it: shown when a save is
 * refused because someone else saved first (rule 2), so the curator sees
 * their changes before acting again.
 */
export function draftDifferences(
  before: FrameworkContent,
  after: FrameworkContent,
): DraftDifference[] {
  const out: DraftDifference[] = [];
  const from = before.version?.sources_required;
  const to = after.version?.sources_required;
  if (from != null && to != null && from !== to) {
    out.push({
      action: 'Changed',
      what: 'Rule',
      code: null,
      title: `Independent sources each area needs: ${from} → ${to}`,
    });
  }
  compare(out, 'Area', before.areas, after.areas, (a) => a.label);
  compare(out, 'Interview type', before.interview_types, after.interview_types, (t) => t.name);
  for (const t of after.interview_types) {
    const old = before.plan_roles[t.id];
    if (old && JSON.stringify(old) !== JSON.stringify(after.plan_roles[t.id] ?? [])) {
      out.push({ action: 'Changed', what: 'Starting plan', code: t.code, title: t.name });
    }
  }
  compare(out, 'Questionnaire', before.questionnaires, after.questionnaires, (q) => q.name);
  const questions = (c: FrameworkContent) => [
    ...c.questionnaires.flatMap((q) => q.questions.map((x) => ({ ...x, title: x.prompt }))),
    ...Object.values(c.area_questions)
      .flat()
      .map((x) => ({ ...x, title: x.text })),
    ...Object.values(c.type_questions)
      .flat()
      .map((x) => ({ ...x, title: x.text })),
  ];
  compare(out, 'Question', questions(before), questions(after), (q) => q.title);
  compare(out, 'Process to map', before.processes, after.processes, (p) => p.name);
  return out;
}

export function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
